import {
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import * as v from "valibot";
import {
  BUDGET_OPTIONS,
  BriefSchema,
  SERVICE_OPTIONS,
  TIMELINE_OPTIONS,
  type Brief,
  type BudgetValue,
  type ServiceValue,
  type TimelineValue,
} from "~/lib/briefs/schema";

const DRAFT_KEY = "jgson.brief-draft.v1";

interface FormDraft {
  name: string;
  email: string;
  service: ServiceValue | "";
  budget: BudgetValue | "";
  timeline: TimelineValue | "";
  message: string;
  honeypot: string;
}

const EMPTY_DRAFT: FormDraft = {
  name: "",
  email: "",
  service: "",
  budget: "",
  timeline: "",
  message: "",
  honeypot: "",
};

interface BriefFormProps {
  onSubmitted: () => void;
}

/**
 * Brief intake form. Validates the same Valibot schema the Hono server
 * re-validates (server/src/index.ts imports it). Persists the in-progress
 * draft to localStorage so visitors who refresh or close the tab don't
 * lose their input.
 *
 * In dev (import.meta.env.DEV), the submit fakes success after a short
 * delay — the Hono server usually isn't running locally. In prod, posts
 * to /api/briefs (via the Cloudflare Tunnel → Hono server) and shows the
 * network error if it fails.
 */
export function BriefForm({ onSubmitted }: BriefFormProps) {
  const [draft, setDraft] = useState<FormDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Partial<Record<keyof Brief, string>>>(
    {},
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Restore any in-progress draft on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setDraft((prev) => ({ ...prev, ...parsed }));
        }
      }
    } catch {
      /* ignore corrupted drafts */
    }
  }, []);

  // Persist the draft on every keystroke (debounced effectively by React).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (draft === EMPTY_DRAFT) return;
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      /* private browsing or full storage — silent fail */
    }
  }, [draft]);

  const update = <K extends keyof FormDraft>(field: K, value: FormDraft[K]) => {
    setDraft((d) => ({ ...d, [field]: value }));
    if (errors[field as keyof Brief]) {
      setErrors((e) => {
        const next = { ...e };
        delete next[field as keyof Brief];
        return next;
      });
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setErrors({});

    const result = v.safeParse(BriefSchema, draft);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof Brief, string>> = {};
      for (const issue of result.issues) {
        const key = issue.path?.[0]?.key;
        if (typeof key === "string" && key in EMPTY_DRAFT) {
          fieldErrors[key as keyof Brief] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);
    try {
      if (import.meta.env.DEV) {
        // No Worker locally — pretend it worked after a brief delay so the
        // visitor can see the loading state during dev.
        await new Promise((r) => window.setTimeout(r, 700));
      } else {
        const res = await fetch("/api/briefs", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(result.output),
        });
        if (!res.ok) {
          const detail = await safeJson(res);
          if (res.status === 429) {
            throw new Error(
              "Chegou muita mensagem desta rede em pouco tempo. Tente de novo em alguns minutos, ou escreva direto para joao@jgson.net.",
            );
          }
          throw new Error(
            detail?.message ??
              `We couldn't send that (HTTP ${res.status}). Try again or email joao@jgson.net directly.`,
          );
        }
      }

      window.localStorage.removeItem(DRAFT_KEY);
      onSubmitted();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Algo deu errado no envio. Tente de novo, ou escreva direto para joao@jgson.net.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className="flex flex-col gap-5"
      aria-describedby={submitError ? "brief-submit-error" : undefined}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="brief-name"
          label="Seu nome"
          error={errors.name}
        >
          <input
            id="brief-name"
            type="text"
            autoComplete="name"
            value={draft.name}
            onChange={(e) => update("name", e.target.value)}
            disabled={submitting}
            className={textInputClass(errors.name)}
          />
        </Field>
        <Field
          id="brief-email"
          label="E-mail"
          error={errors.email}
        >
          <input
            id="brief-email"
            type="email"
            autoComplete="email"
            value={draft.email}
            onChange={(e) => update("email", e.target.value)}
            disabled={submitting}
            className={textInputClass(errors.email)}
          />
        </Field>
      </div>

      <Field
        id="brief-service"
        label="Qual é a área?"
        error={errors.service}
      >
        <Select
          id="brief-service"
          value={draft.service}
          onChange={(value) => update("service", value as ServiceValue | "")}
          disabled={submitting}
          hasError={Boolean(errors.service)}
          placeholder="Escolha uma área…"
          options={SERVICE_OPTIONS}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="brief-budget" label="Faixa de orçamento" error={errors.budget}>
          <Select
            id="brief-budget"
            value={draft.budget}
            onChange={(value) => update("budget", value as BudgetValue | "")}
            disabled={submitting}
            hasError={Boolean(errors.budget)}
            placeholder="Escolha uma faixa…"
            options={BUDGET_OPTIONS}
          />
        </Field>
        <Field
          id="brief-timeline"
          label="Prazo"
          error={errors.timeline}
        >
          <Select
            id="brief-timeline"
            value={draft.timeline}
            onChange={(value) =>
              update("timeline", value as TimelineValue | "")
            }
            disabled={submitting}
            hasError={Boolean(errors.timeline)}
            placeholder="Para quando?"
            options={TIMELINE_OPTIONS}
          />
        </Field>
      </div>

      <Field
        id="brief-message"
        label="Conte o problema"
        hint={`${draft.message.length} / 2000`}
        error={errors.message}
      >
        <textarea
          id="brief-message"
          rows={6}
          value={draft.message}
          onChange={(e) => update("message", e.target.value)}
          disabled={submitting}
          maxLength={2000}
          className={[
            "w-full resize-y rounded-xl border bg-jg-bg/40 px-3 py-2.5 text-sm text-jg-fg outline-none transition-colors",
            "placeholder:text-jg-fg-muted focus:border-jg-fg disabled:opacity-60",
            errors.message ? "border-jg-play" : "border-jg-border",
          ].join(" ")}
          placeholder="Que problema você quer resolver? Para quem? Tem restrição de plataforma, prazo ou time?"
        />
      </Field>

      {/* Honeypot — visually hidden, hidden from a11y. Real users can't see
          or focus it; naive bots fill all visible inputs. */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="brief-website">Website (leave blank)</label>
        <input
          id="brief-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={draft.honeypot}
          onChange={(e) => update("honeypot", e.target.value)}
        />
      </div>

      {submitError && (
        <p
          id="brief-submit-error"
          role="alert"
          className="rounded-xl border border-jg-play/40 bg-jg-play/10 px-3 py-2 text-sm text-jg-fg"
        >
          {submitError}
        </p>
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        <p className="text-xs text-jg-fg-muted">
          We reply within 24h.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-jg-fg px-5 py-2 text-sm font-semibold text-jg-bg transition-opacity disabled:opacity-50 enabled:hover:opacity-90"
        >
          {submitting ? "Enviando…" : "Enviar →"}
        </button>
      </div>
    </form>
  );
}

interface FieldProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}

function Field({ id, label, hint, error, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={id}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-jg-fg-muted"
        >
          {label}
        </label>
        {hint && (
          <span className="font-mono text-[10px] text-jg-fg-muted">
            {hint}
          </span>
        )}
      </div>
      {children}
      {error && (
        <p className="text-xs text-jg-play" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function textInputClass(error?: string): string {
  return [
    "w-full rounded-xl border bg-jg-bg/40 px-3 py-2 text-sm text-jg-fg outline-none transition-colors",
    "placeholder:text-jg-fg-muted focus:border-jg-fg disabled:opacity-60",
    error ? "border-jg-play" : "border-jg-border",
  ].join(" ");
}

interface SelectProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder: string;
  options: readonly { value: string; label: string }[];
}

function Select({
  id,
  value,
  onChange,
  disabled,
  hasError,
  placeholder,
  options,
}: SelectProps) {
  const handle = (e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value);
  return (
    <select
      id={id}
      value={value}
      onChange={handle}
      disabled={disabled}
      className={[
        "w-full appearance-none rounded-xl border bg-jg-bg/40 px-3 py-2 text-sm text-jg-fg outline-none transition-colors",
        "focus:border-jg-fg disabled:opacity-60",
        hasError ? "border-jg-play" : "border-jg-border",
      ].join(" ")}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

async function safeJson(res: Response): Promise<{ message?: string } | null> {
  try {
    return (await res.json()) as { message?: string };
  } catch {
    return null;
  }
}
